import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { normalizePhone, signature } from "@/components/ShareDialog";
import { api, errMsg } from "@/lib/api";

export const inputCls = "w-full rounded-md border border-[#D4E6EC] bg-white px-3 py-2 text-sm text-[#0D2B3A] focus:outline-none focus:ring-2 focus:ring-[#0B6E8F]/30 focus:border-[#0B6E8F] disabled:bg-[#EAF5F7] transition-colors";

export const PageHead = ({ eyebrow, title, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 fade-up">
    <div>
      <div className="text-xs uppercase tracking-[0.25em] text-[#7B929C]">{eyebrow}</div>
      <h1 className="font-serif-it text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-2">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Panel = ({ title, testId, right, children, className = "" }) => (
  <section className={`rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-6 space-y-4 ${className}`} data-testid={testId}>
    {(title || right) && <div className="flex items-center justify-between gap-3 flex-wrap"><h2 className="font-serif-it text-2xl font-semibold">{title}</h2>{right}</div>}
    {children}
  </section>
);

export const Field = ({ label, children, wide }) => (
  <label className={`block text-xs font-medium text-[#47606B] space-y-1 ${wide ? "sm:col-span-2" : ""}`}><span>{label}</span>{children}</label>
);

export const Modal = ({ open, onOpenChange, title, testId, children }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid={testId}>
      <DialogHeader><DialogTitle className="font-serif-it text-2xl">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export function Stat({ label, value, sub, testId, dark }) {
  return (
    <div className={`rounded-xl p-5 ${dark ? "bg-gradient-to-br from-[#0B6E8F] to-[#0D2B3A] text-white" : "border border-[#D4E6EC] bg-white"}`} data-testid={testId}>
      <div className={`text-xs ${dark ? "text-white/70" : "text-[#7B929C]"}`}>{label}</div>
      <div className="font-serif-it text-3xl font-semibold mt-1">{value}</div>
      {sub && <div className={`text-xs mt-1 ${dark ? "text-white/60" : "text-[#7B929C]"}`}>{sub}</div>}
    </div>
  );
}

export function useWa() {
  const { user } = useAuth();
  return (phone, text) => {
    const n = normalizePhone(phone);
    window.open(`https://api.whatsapp.com/send?phone=${n}&text=${encodeURIComponent(`${text}\n\n${signature(user)}`)}`, "_blank");
  };
}

export function WaButton({ phone, text, testId, label = "WhatsApp", onSent }) {
  const wa = useWa();
  return (
    <Button size="sm" variant="outline" disabled={!normalizePhone(phone)} title={normalizePhone(phone) ? "" : "Numero di telefono mancante"} data-testid={testId}
      className="rounded-full border-[#25D366] text-[#128C4A] hover:bg-[#25D366]/10" onClick={() => { wa(phone, text); onSent && onSent(); }}>
      <MessageCircle size={14} className="mr-1.5" /> {label}
    </Button>
  );
}

export function useList(url) {
  const [rows, setRows] = useState(null);
  const reload = useCallback(() => api.get(url).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [url]);
  useEffect(() => {
    reload();
  }, [reload]);
  return [rows, reload];
}

export async function saveDoc(url, id, body) {
  try {
    const r = id ? await api.put(`${url}/${id}`, body) : await api.post(url, body);
    toast.success("Salvato");
    return r.data;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
}

export const itDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
